// ==================== SISTEMA DE ARMAZENAMENTO OFFLINE PARA APP DO EMPREGADO ====================
// IndexedDB para justificativas, solicitações e dados do usuário em cache

const DB_NAME = 'jornyx_empregado_offline';
const DB_VERSION = 1;

const STORES = {
  USUARIO_CACHE: 'usuario_cache',
  JUSTIFICATIVAS_PENDENTES: 'justificativas_pendentes',
  SOLICITACOES_PENDENTES: 'solicitacoes_pendentes',
  JORNADAS_CACHE: 'jornadas_cache',
  SYNC_LOG: 'sync_log'
};

let dbInstance = null;

// Inicializar IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('[EmpregadoOffline] Banco inicializado');
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Cache de dados do usuário logado
      if (!db.objectStoreNames.contains(STORES.USUARIO_CACHE)) {
        const userStore = db.createObjectStore(STORES.USUARIO_CACHE, { keyPath: 'empregado_id' });
        userStore.createIndex('cpf', 'cpf', { unique: false });
      }

      // Justificativas criadas offline
      if (!db.objectStoreNames.contains(STORES.JUSTIFICATIVAS_PENDENTES)) {
        const justStore = db.createObjectStore(STORES.JUSTIFICATIVAS_PENDENTES, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        justStore.createIndex('empregado_id', 'empregado_id', { unique: false });
        justStore.createIndex('synced', 'synced', { unique: false });
        justStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Solicitações de exclusão de marcação
      if (!db.objectStoreNames.contains(STORES.SOLICITACOES_PENDENTES)) {
        const solStore = db.createObjectStore(STORES.SOLICITACOES_PENDENTES, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        solStore.createIndex('empregado_id', 'empregado_id', { unique: false });
        solStore.createIndex('synced', 'synced', { unique: false });
      }

      // Cache de jornadas para visualização offline
      if (!db.objectStoreNames.contains(STORES.JORNADAS_CACHE)) {
        const jornadaStore = db.createObjectStore(STORES.JORNADAS_CACHE, { keyPath: 'cache_key' });
        jornadaStore.createIndex('empregado_id', 'empregado_id', { unique: false });
        jornadaStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Log de sincronização
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const syncStore = db.createObjectStore(STORES.SYNC_LOG, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('tipo', 'tipo', { unique: false });
      }
    };
  });
};

// ==================== CACHE DO USUÁRIO ====================

export const cacheUsuario = async (userData) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.USUARIO_CACHE, 'readwrite');
    const store = tx.objectStore(STORES.USUARIO_CACHE);
    
    const data = {
      ...userData,
      empregado_id: userData.empregado_id || userData.id,
      cached_at: new Date().toISOString()
    };
    
    const request = store.put(data);
    request.onsuccess = () => {
      console.log('[EmpregadoOffline] Usuário cacheado:', data.nome);
      resolve(data);
    };
    request.onerror = () => reject(request.error);
  });
};

export const getUsuarioCache = async (cpf) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.USUARIO_CACHE, 'readonly');
    const store = tx.objectStore(STORES.USUARIO_CACHE);
    const index = store.index('cpf');
    const request = index.get(cpf);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// ==================== JUSTIFICATIVAS OFFLINE ====================

export const salvarJustificativaOffline = async (justificativa, empregadoId) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JUSTIFICATIVAS_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORES.JUSTIFICATIVAS_PENDENTES);
    
    const data = {
      ...justificativa,
      empregado_id: empregadoId,
      synced: false,
      created_at: new Date().toISOString(),
      offline_id: `offline_just_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    const request = store.add(data);
    request.onsuccess = () => {
      console.log('[EmpregadoOffline] Justificativa salva offline:', data.offline_id);
      logSync('justificativa_salva_offline', { offline_id: data.offline_id });
      resolve({ ...data, id: request.result });
    };
    request.onerror = () => reject(request.error);
  });
};

export const getJustificativasPendentes = async (empregadoId = null) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JUSTIFICATIVAS_PENDENTES, 'readonly');
    const store = tx.objectStore(STORES.JUSTIFICATIVAS_PENDENTES);
    const index = store.index('synced');
    const request = index.getAll(IDBKeyRange.only(false));
    
    request.onsuccess = () => {
      let results = request.result || [];
      if (empregadoId) {
        results = results.filter(j => j.empregado_id === empregadoId);
      }
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const marcarJustificativaSincronizada = async (id, serverResponse) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JUSTIFICATIVAS_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORES.JUSTIFICATIVAS_PENDENTES);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const just = getRequest.result;
      if (just) {
        just.synced = true;
        just.synced_at = new Date().toISOString();
        just.server_id = serverResponse?.id;
        
        const updateRequest = store.put(just);
        updateRequest.onsuccess = () => resolve(just);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        resolve(null);
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// ==================== SOLICITAÇÕES DE EXCLUSÃO ====================

export const salvarSolicitacaoExclusaoOffline = async (solicitacao, empregadoId) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SOLICITACOES_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORES.SOLICITACOES_PENDENTES);
    
    const data = {
      ...solicitacao,
      empregado_id: empregadoId,
      synced: false,
      created_at: new Date().toISOString(),
      offline_id: `offline_sol_${Date.now()}`
    };
    
    const request = store.add(data);
    request.onsuccess = () => {
      console.log('[EmpregadoOffline] Solicitação salva offline');
      resolve({ ...data, id: request.result });
    };
    request.onerror = () => reject(request.error);
  });
};

export const getSolicitacoesPendentes = async (empregadoId = null) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SOLICITACOES_PENDENTES, 'readonly');
    const store = tx.objectStore(STORES.SOLICITACOES_PENDENTES);
    const index = store.index('synced');
    const request = index.getAll(IDBKeyRange.only(false));
    
    request.onsuccess = () => {
      let results = request.result || [];
      if (empregadoId) {
        results = results.filter(s => s.empregado_id === empregadoId);
      }
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const marcarSolicitacaoSincronizada = async (id) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SOLICITACOES_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORES.SOLICITACOES_PENDENTES);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const sol = getRequest.result;
      if (sol) {
        sol.synced = true;
        sol.synced_at = new Date().toISOString();
        store.put(sol);
      }
      resolve(sol);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// ==================== CACHE DE JORNADAS ====================

export const cacheJornadas = async (jornadas, empregadoId, periodo) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JORNADAS_CACHE, 'readwrite');
    const store = tx.objectStore(STORES.JORNADAS_CACHE);
    
    const cacheKey = `${empregadoId}_${periodo.data_inicio}_${periodo.data_fim}`;
    const data = {
      cache_key: cacheKey,
      empregado_id: empregadoId,
      periodo,
      jornadas,
      cached_at: new Date().toISOString()
    };
    
    const request = store.put(data);
    request.onsuccess = () => {
      console.log('[EmpregadoOffline] Jornadas cacheadas:', jornadas.length);
      resolve(data);
    };
    request.onerror = () => reject(request.error);
  });
};

export const getJornadasCache = async (empregadoId, periodo) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JORNADAS_CACHE, 'readonly');
    const store = tx.objectStore(STORES.JORNADAS_CACHE);
    
    const cacheKey = `${empregadoId}_${periodo.data_inicio}_${periodo.data_fim}`;
    const request = store.get(cacheKey);
    
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        // Verificar se cache tem menos de 1 hora
        const cachedAt = new Date(result.cached_at);
        const agora = new Date();
        const diffHoras = (agora - cachedAt) / (1000 * 60 * 60);
        
        if (diffHoras < 1) {
          resolve(result.jornadas);
        } else {
          resolve(null); // Cache expirado
        }
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// ==================== SINCRONIZAÇÃO ====================

export const sincronizarDadosPendentes = async (api, token) => {
  if (!navigator.onLine) {
    console.log('[EmpregadoOffline] Sem conexão, sincronização adiada');
    return { success: false, reason: 'offline' };
  }

  const resultados = {
    justificativas: { enviadas: 0, erros: 0 },
    solicitacoes: { enviadas: 0, erros: 0 }
  };

  try {
    // 1. Sincronizar justificativas
    const justificativas = await getJustificativasPendentes();
    
    for (const just of justificativas) {
      try {
        const response = await fetch(`${api}/api/app/justificativas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            tipo: just.tipo,
            motivo: just.motivo,
            descricao: just.descricao,
            data_inconsistencia: just.data_inconsistencia,
            offline_id: just.offline_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          await marcarJustificativaSincronizada(just.id, data);
          resultados.justificativas.enviadas++;
        } else {
          resultados.justificativas.erros++;
        }
      } catch (error) {
        console.error('[EmpregadoOffline] Erro ao sincronizar justificativa:', error);
        resultados.justificativas.erros++;
      }
    }

    // 2. Sincronizar solicitações de exclusão
    const solicitacoes = await getSolicitacoesPendentes();
    
    for (const sol of solicitacoes) {
      try {
        const response = await fetch(`${api}/api/app/solicitar-exclusao-marcacao`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            marcacao_id: sol.marcacao_id,
            motivo: sol.motivo,
            offline_id: sol.offline_id
          })
        });

        if (response.ok) {
          await marcarSolicitacaoSincronizada(sol.id);
          resultados.solicitacoes.enviadas++;
        } else {
          resultados.solicitacoes.erros++;
        }
      } catch (error) {
        console.error('[EmpregadoOffline] Erro ao sincronizar solicitação:', error);
        resultados.solicitacoes.erros++;
      }
    }

    await logSync('sincronizacao_completa', resultados);
    console.log('[EmpregadoOffline] Sincronização concluída:', resultados);
    
    return { success: true, resultados };
  } catch (error) {
    console.error('[EmpregadoOffline] Erro na sincronização:', error);
    return { success: false, error };
  }
};

// ==================== SYNC LOG ====================

const logSync = async (tipo, dados) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.SYNC_LOG, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_LOG);
    
    store.add({
      tipo,
      dados,
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[EmpregadoOffline] Erro ao registrar log:', e);
  }
};

// ==================== STATUS E ESTATÍSTICAS ====================

export const getStatusOffline = async (empregadoId = null) => {
  const [justificativas, solicitacoes] = await Promise.all([
    getJustificativasPendentes(empregadoId),
    getSolicitacoesPendentes(empregadoId)
  ]);

  return {
    isOnline: navigator.onLine,
    justificativas_pendentes: justificativas.length,
    solicitacoes_pendentes: solicitacoes.length,
    total_pendentes: justificativas.length + solicitacoes.length,
    ultima_verificacao: new Date().toISOString()
  };
};

export const limparDadosSincronizados = async () => {
  const db = await initDB();
  
  // Limpar justificativas sincronizadas
  const txJust = db.transaction(STORES.JUSTIFICATIVAS_PENDENTES, 'readwrite');
  const storeJust = txJust.objectStore(STORES.JUSTIFICATIVAS_PENDENTES);
  const indexJust = storeJust.index('synced');
  
  const reqJust = indexJust.openCursor(IDBKeyRange.only(true));
  reqJust.onsuccess = (event) => {
    const cursor = event.target.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
    }
  };

  // Limpar solicitações sincronizadas
  const txSol = db.transaction(STORES.SOLICITACOES_PENDENTES, 'readwrite');
  const storeSol = txSol.objectStore(STORES.SOLICITACOES_PENDENTES);
  const indexSol = storeSol.index('synced');
  
  const reqSol = indexSol.openCursor(IDBKeyRange.only(true));
  reqSol.onsuccess = (event) => {
    const cursor = event.target.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
    }
  };

  console.log('[EmpregadoOffline] Dados sincronizados limpos');
};
